import { CloseOutlined, FileAddOutlined } from '@ant-design/icons';
import { App, Button, DatePicker, Popconfirm, Skeleton, Spin, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import { type FC, useEffect, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import dayjs from '@/utils/dayjs-instance';
import { formatError, logError } from '@/utils/formatError';
import {
  useDeleteDriverHoliday,
  useGetDriverHolidays,
  useInsertDriverHoliday,
} from '../../query/driverHolidays.hooks';

interface DriverHolidaysProps {
  driverId: string;
  onHolidaysChange: (holidays: Record<string, 'holiday' | 'forced'>) => void;
}

export const DriverHolidays: FC<DriverHolidaysProps> = ({ driverId, onHolidaysChange }) => {
  const { message } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [isForced, setIsForced] = useState(false);
  const [deletingHolidayId, setDeletingHolidayId] = useState<number | null>(null);

  const { data, isLoading } = useGetDriverHolidays({ id: driverId });
  const { mutateAsync: insertDriverHoliday, isPending: insertDriverHolidayLoading } =
    useInsertDriverHoliday();
  const { mutateAsync: deleteDriverHoliday } = useDeleteDriverHoliday();

  useEffect(() => {
    if (data?.user_by_pk?.driver_holidays) {
      const holidays = data.user_by_pk.driver_holidays.reduce(
        (acc, holiday) => {
          const formattedDate = dayjs.utc(holiday.date).format('YYYY-MM-DD');
          acc[formattedDate] = holiday.forced ? 'forced' : 'holiday';
          return acc;
        },
        {} as Record<string, 'forced' | 'holiday'>
      );
      onHolidaysChange(holidays);
    }
  }, [data, onHolidaysChange]);

  useEffect(() => {
    if (!isModalVisible) {
      setSelectedDate(null);
      setIsForced(false);
    }
  }, [isModalVisible]);

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const handleAddHoliday = async (forced: boolean) => {
    if (selectedDate && dayjs(selectedDate).isSameOrAfter(dayjs().startOf('day'))) {
      console.log(selectedDate);
      try {
        await insertDriverHoliday({
          driverId,
          date: dayjs.utc(dayjs(selectedDate).format('YYYY-MM-DD')).startOf('day'),
          forced: forced,
        });
        message.success('Holiday/Forced workday added successfully');
        setIsModalVisible(false);
      } catch (err) {
        const errorMessage = formatError(err, 'Add Holiday/Forced Workday');
        logError(err, 'Add Holiday/Forced Workday');
        message.error(`Failed to add Holiday/Forced workday: ${errorMessage}`);
      }
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    setDeletingHolidayId(id);
    try {
      await deleteDriverHoliday({ id });
      message.success('Holiday/Forced workday deleted successfully');
    } catch (err) {
      const errorMessage = formatError(err, 'Delete Holiday/Forced Workday');
      logError(err, 'Delete Holiday/Forced Workday');
      message.error(`Failed to delete Holiday/Forced workday: ${errorMessage}`);
    } finally {
      setDeletingHolidayId(null);
    }
  };

  const filteredHolidays = data?.user_by_pk?.driver_holidays
    .filter((holiday) => dayjs.utc(holiday.date).isBefore(dayjs().add(30, 'days')))
    .sort((a, b) => dayjs.utc(a.date).diff(dayjs.utc(b.date)));

  const isInitialLoad = isLoading && !data;

  return (
    <Spin spinning={isLoading && !isInitialLoad}>
      <div style={{ marginLeft: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ color: 'white' }}>Days-off & Forced workdays:</span>
          {isInitialLoad ? (
            Array.from({ length: 5 }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
              <Skeleton.Button key={i} active size="small" style={{ width: 90, height: 22 }} />
            ))
          ) : (
            <>
              {filteredHolidays?.map((holiday) => {
                const isDeleting = deletingHolidayId === holiday.id;

                return (
                  <Tag
                    key={holiday.id}
                    color={holiday.forced ? 'green' : 'red'}
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                    closable={!isDeleting}
                    closeIcon={
                      <Popconfirm
                        key={holiday.id}
                        title="Are you sure to delete this date?"
                        onConfirm={() => handleDeleteHoliday(holiday.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <CloseOutlined />
                      </Popconfirm>
                    }
                    onClose={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {dayjs.utc(holiday.date).format('DD.MM.YYYY')}
                    {isDeleting && <Spin size="small" />}
                  </Tag>
                );
              })}
              <Button
                danger
                type={'primary'}
                icon={<FileAddOutlined />}
                size={'small'}
                onClick={() => {
                  setIsForced(false);
                  setIsModalVisible(true);
                }}
              >
                Add Day off
              </Button>
              <Button
                style={{ backgroundColor: '#389e0d', color: 'white' }}
                type={'primary'}
                icon={<FileAddOutlined />}
                size={'small'}
                onClick={() => {
                  setIsForced(true);
                  setIsModalVisible(true);
                }}
              >
                Add Forced Workday
              </Button>
            </>
          )}
        </div>
      </div>

      <AppModal
        title={`Select the date of ${isForced ? 'forced workday' : 'holiday'}`}
        open={isModalVisible}
        onOk={() => handleAddHoliday(isForced)}
        onCancel={() => setIsModalVisible(false)}
        okText="Save"
        okButtonProps={{
          loading: insertDriverHolidayLoading,
        }}
        cancelButtonProps={{
          disabled: insertDriverHolidayLoading,
        }}
      >
        <DatePicker
          onChange={handleDateChange}
          disabledDate={(current) => current && current < dayjs().startOf('day')}
          value={selectedDate}
        />
      </AppModal>
    </Spin>
  );
};
